import transUnit from '@/utils/transStorageUnit';

import { normalizeQuantityUnit } from './resolve';

/** 可与 Gi 互转的存储/内存计费单位 */
export const STORAGE_BILLING_UNITS = ['Byte', 'Ki', 'Mi', 'Gi', 'Ti'] as const;

export type StorageBillingUnit = (typeof STORAGE_BILLING_UNITS)[number];

/** 展示升单位默认候选（不含 Byte/Ki，避免可读性回退） */
const DEFAULT_CAPACITY_DISPLAY_UNITS: StorageBillingUnit[] = ['Mi', 'Gi', 'Ti'];

/** 是否为存储类计费单位（Byte/Ki/Mi/Gi/Ti） */
export function isStorageBillingUnit(unit?: string): unit is StorageBillingUnit {
  return !!unit && (STORAGE_BILLING_UNITS as readonly string[]).includes(unit);
}

/**
 * 表单/展示数量为 Gi；提交/计价时按计费项 quantityUnit 换算。
 * 非存储单位、已是 Gi、或无法转换时返回原文本。
 */
export function convertFormGiToBillingRequirement(quantityGi: unknown, billingUnit?: string): string | undefined {
  const text = quantityGi == null ? '' : String(quantityGi).trim();
  if (!text) return undefined;

  const unit = normalizeQuantityUnit(billingUnit);
  if (!unit || unit === 'Gi' || !isStorageBillingUnit(unit)) return text;

  try {
    const converted = transUnit({ value: text, unit: 'Gi', targetUnit: unit, precision: 0 });
    return String(converted);
  } catch {
    return text;
  }
}

/** 解析「数值 + 可选单位后缀」；支持 983040Mi / 983040 + unit */
function parseCapacityAmountAndUnit(value: unknown, unit?: unknown): { amount: string; unit: string } | null {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const explicitUnit = normalizeQuantityUnit(unit);
  for (let i = STORAGE_BILLING_UNITS.length - 1; i >= 0; i -= 1) {
    const suffix = STORAGE_BILLING_UNITS[i];
    if (raw.endsWith(suffix) && raw.length > suffix.length) {
      const amount = raw.slice(0, -suffix.length).trim();
      if (amount && /^-?\d+(\.\d+)?$/.test(amount)) {
        return { amount, unit: explicitUnit || suffix };
      }
    }
  }

  if (/^-?\d+(\.\d+)?$/.test(raw)) {
    return { amount: raw, unit: explicitUnit || '' };
  }

  return explicitUnit ? { amount: raw, unit: explicitUnit } : { amount: raw, unit: '' };
}

function normalizeSupportedUnits(supported: unknown, fallbackUnit: StorageBillingUnit): StorageBillingUnit[] {
  const fromSupported = Array.isArray(supported)
    ? supported.map((item) => normalizeQuantityUnit(item)).filter((item): item is StorageBillingUnit => isStorageBillingUnit(item))
    : [];
  const base = fromSupported.length ? fromSupported : [...DEFAULT_CAPACITY_DISPLAY_UNITS];
  if (!base.includes(fallbackUnit)) base.push(fallbackUnit);
  return base;
}

/**
 * 展示用容量升单位：在 supported（缺省 Mi/Gi/Ti）内优先选更大且能整除的单位。
 * 仅 UI；提交/resource.size 勿经此函数改写。
 * @example formatCapacityForDisplay(983040, 'Mi', ['Mi','Gi','Ti']) → '960Gi'
 */
export function formatCapacityForDisplay(value: unknown, unit?: unknown, supported?: unknown): string {
  const parsed = parseCapacityAmountAndUnit(value, unit);
  if (!parsed) return '';

  const { amount, unit: rawUnit } = parsed;
  if (!rawUnit) return amount;
  if (!isStorageBillingUnit(rawUnit)) return `${amount}${rawUnit}`;

  const currentIdx = STORAGE_BILLING_UNITS.indexOf(rawUnit);
  const candidates = normalizeSupportedUnits(supported, rawUnit)
    .filter((item) => STORAGE_BILLING_UNITS.indexOf(item) >= currentIdx)
    .sort((a, b) => STORAGE_BILLING_UNITS.indexOf(b) - STORAGE_BILLING_UNITS.indexOf(a));

  const amountNum = Number(amount);
  for (const target of candidates) {
    if (target === rawUnit) continue;
    try {
      const converted = transUnit({ value: amount, unit: rawUnit, targetUnit: target, precision: 0 });
      if (!Number.isFinite(converted) || converted <= 0) continue;
      /** 往返校验，确保仅在 1024 整除时升单位 */
      const roundTrip = transUnit({ value: converted, unit: target, targetUnit: rawUnit, precision: 0 });
      if (Number(roundTrip) === amountNum) {
        return `${converted}${target}`;
      }
    } catch {
      // 换算失败则尝试下一候选
    }
  }

  return `${amount}${rawUnit}`;
}
